import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

const TABS = ['broadcast', 'computer', 'history', 'test'];

const TYPE_ICONS = {
  topup: '💳', booking_confirmed: '✅', booking_cancelled: '❌',
  session_warning: '⏰', session_ended: '🎮', broadcast: '📢', test: '🔔',
};

export default function Notifications() {
  const { t } = useLang();
  const TAB_LABELS = { broadcast: t('tabBroadcast'), computer: t('tabComputer'), history: t('tabHistory'), test: t('tabTest') };
  const { labId } = useLab();
  const [tab, setTab] = useState('broadcast');
  const [fcmStatus, setFcmStatus] = useState(null);
  const [computers, setComputers] = useState([]);

  // Computer message state (lab = joriy filial)
  const pcLab = labId;
  const [pcId, setPcId] = useState('');
  const [pcMsg, setPcMsg] = useState('');
  const [pcLoading, setPcLoading] = useState(false);
  const [pcResult, setPcResult] = useState(null);
  const [pcHistory, setPcHistory] = useState([]);

  // Broadcast state (lab = joriy filial)
  const bcLab = labId;
  const [bcTitle, setBcTitle] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcLoading, setBcLoading] = useState(false);
  const [bcResult, setBcResult] = useState(null);

  // History state
  const [history, setHistory] = useState([]);
  const histLab = labId; // joriy filial
  const [histLoading, setHistLoading] = useState(false);

  // Test state
  const [testToken, setTestToken] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get('/notifications/status').then(r => setFcmStatus(r.data.configured)).catch(() => setFcmStatus(false));
  }, []);

  useEffect(() => {
    if (!pcLab) return;
    api.get('/owner/computers').then(r => {
      const filtered = r.data.filter(c => String(c.lab_id) === String(pcLab));
      setComputers(filtered);
      if (filtered[0]) setPcId(filtered[0].id);
    }).catch(() => {});
  }, [pcLab]);

  useEffect(() => {
    if (tab === 'computer' && pcId) loadPcHistory();
  }, [tab, pcId]); // eslint-disable-line

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const url = histLab ? `/notifications/admin-history?lab_id=${histLab}` : '/notifications/admin-history';
      const r = await api.get(url);
      setHistory(r.data);
    } catch { setHistory([]); }
    setHistLoading(false);
  }, [histLab]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  const sendBroadcast = async () => {
    if (!bcLab || !bcTitle.trim() || !bcBody.trim()) return;
    setBcLoading(true); setBcResult(null);
    try {
      const r = await api.post('/notifications/broadcast', { lab_id: bcLab, title: bcTitle, body: bcBody });
      setBcResult({ ok: true, msg: `✅ ${r.data.sent_to}` });
      setBcTitle(''); setBcBody('');
    } catch (e) {
      setBcResult({ ok: false, msg: e.response?.data?.error || t('error') });
    }
    setBcLoading(false);
  };

  const loadPcHistory = async () => {
    if (!pcId) return;
    try {
      const r = await api.get(`/owner/computers/${pcId}/messages`);
      setPcHistory(r.data);
    } catch { setPcHistory([]); }
  };

  const sendPcMessage = async () => {
    if (!pcId || !pcMsg.trim()) return;
    setPcLoading(true); setPcResult(null);
    try {
      await api.post(`/owner/computers/${pcId}/message`, { message: pcMsg });
      setPcResult({ ok: true, msg: `✅ ${t('sendBtn')}` });
      setPcMsg('');
      loadPcHistory();
    } catch (e) {
      setPcResult({ ok: false, msg: e.response?.data?.error || t('error') });
    }
    setPcLoading(false);
  };

  const sendTest = async () => {
    if (!testToken.trim()) return;
    setTestLoading(true); setTestResult(null);
    try {
      await api.post('/notifications/test-send', { token: testToken });
      setTestResult({ ok: true, msg: `✅ ${t('testTitle')}` });
    } catch (e) {
      setTestResult({ ok: false, msg: e.response?.data?.error || t('error') });
    }
    setTestLoading(false);
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: 'var(--text)' }}>{t('notificationsTitle')}</h2>
        <FcmBadge configured={fcmStatus} />
      </div>


      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {TABS.map(tabKey => (
          <button key={tabKey} onClick={() => setTab(tabKey)} style={{
            padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
            background: tab === tabKey ? 'rgba(0,212,255,0.1)' : 'var(--bg2)',
            border: `1px solid ${tab === tabKey ? 'var(--cyan)' : 'var(--border)'}`,
            color: tab === tabKey ? 'var(--cyan)' : 'var(--text2)',
          }}>{TAB_LABELS[tabKey]}</button>
        ))}
      </div>

      {/* BROADCAST TAB */}
      {tab === 'broadcast' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 18 }}>{t('broadcastSendTitle')}</div>

            <Label>{t('titleLabel')}</Label>
            <input value={bcTitle} onChange={e => setBcTitle(e.target.value)} placeholder={t('titlePlhNotif')}
              style={inputStyle} maxLength={80} />

            <Label>{t('bodyLabel')}</Label>
            <textarea value={bcBody} onChange={e => setBcBody(e.target.value)}
              placeholder={t('bodyPlhNotif')} rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} maxLength={300} />

            {bcResult && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                background: bcResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${bcResult.ok ? 'var(--green)' : 'var(--red)'}`,
                color: bcResult.ok ? 'var(--green)' : 'var(--red)',
              }}>{bcResult.msg}</div>
            )}

            <button onClick={sendBroadcast} disabled={bcLoading || !bcTitle.trim() || !bcBody.trim()}
              style={{ ...btnStyle, width: '100%', opacity: bcLoading || !bcTitle.trim() || !bcBody.trim() ? 0.5 : 1 }}>
              {bcLoading ? t('sending') : t('sendBtn')}
            </button>

            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text3)' }}>
              {t('broadcastFootnote')}
            </div>
          </div>

          <BroadcastTemplates onSelect={(t, b) => { setBcTitle(t); setBcBody(b); }} />
        </div>
      )}

      {/* HISTORY TAB */}
      {tab === 'history' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <button onClick={loadHistory} style={{ ...btnStyle, padding: '8px 16px' }}>
              {t('refreshBtn')}
            </button>
            <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>
              {history.length}
            </span>
          </div>

          {histLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>{t('loading')}</div>
          ) : history.length === 0 ? (
            <Empty text={t('noNotifications')} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {history.map(n => <NotifRow key={n.id} n={n} />)}
            </div>
          )}
        </div>
      )}

      {/* COMPUTER MESSAGE TAB */}
      {tab === 'computer' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{t('pcMsgTitle')}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>
              {t('pcMsgDesc')}
            </div>

            <Label>{t('pcSelectLabel')}</Label>
            <select value={pcId} onChange={e => { setPcId(e.target.value); setPcHistory([]); }}
              style={selectStyle}>
              {computers.map(c => (
                <option key={c.id} value={c.id}>
                  PC #{c.number} — {c.status === 'busy' ? `🔴 ${t('pcBusy')}` : `🟢 ${t('pcFree')}`}
                </option>
              ))}
            </select>

            <Label>{t('pcMsgLabel')}</Label>
            <textarea value={pcMsg} onChange={e => setPcMsg(e.target.value)}
              placeholder={t('pcMsgPlh')} rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 70 }} maxLength={200} />
            <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'right', marginTop: 3 }}>
              {pcMsg.length}/200
            </div>

            {pcResult && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13, marginTop: 12,
                background: pcResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${pcResult.ok ? 'var(--green)' : 'var(--red)'}`,
                color: pcResult.ok ? 'var(--green)' : 'var(--red)',
              }}>{pcResult.msg}</div>
            )}

            <button onClick={sendPcMessage} disabled={pcLoading || !pcMsg.trim() || !pcId}
              style={{ ...btnStyle, width: '100%', marginTop: 14,
                opacity: pcLoading || !pcMsg.trim() || !pcId ? 0.5 : 1 }}>
              {pcLoading ? t('sending') : t('sendToPcBtn')}
            </button>

            <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, letterSpacing: '1px', textTransform: 'uppercase' }}>
                {t('quickMsgsTitle')}
              </div>
              {[
                '⏰ 10 daqiqada filial yopiladi',
                "🎮 Iltimos, o'yinni saqlang va tugatishga tayyorlaning",
                '📢 Siz band qilgan vaqt boshlanmoqda, yakunlang',
                "🔧 Tez orada texnik ko'rikdan o'tkaziladi",
              ].map((msg, i) => (
                <button key={i} onClick={() => setPcMsg(msg)} style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  marginBottom: 6, padding: '8px 12px', borderRadius: 7,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                  color: 'var(--text2)', fontSize: 12, cursor: 'pointer',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cyan)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>{msg}</button>
              ))}
            </div>
          </div>

          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: 'var(--text)' }}>{t('sentMsgsTitle')}</div>
              <button onClick={loadPcHistory} style={{
                background: 'transparent', border: '1px solid var(--border)',
                borderRadius: 6, padding: '4px 10px', color: 'var(--text3)', cursor: 'pointer', fontSize: 12,
              }}>🔄</button>
            </div>
            {pcHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)', fontSize: 13 }}>
                {t('noMsgsSent')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
                {pcHistory.map(m => (
                  <div key={m.id} style={{
                    background: 'var(--bg)', borderRadius: 10, padding: '10px 14px',
                    border: `1px solid ${m.read_at ? 'var(--border)' : 'rgba(0,212,255,0.3)'}`,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: 'var(--cyan)' }}>{m.sender_name}</span>
                      <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                        {new Date(m.created_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text)' }}>{m.message}</div>
                    <div style={{ fontSize: 10, marginTop: 4,
                      color: m.read_at ? 'var(--green)' : 'var(--text3)' }}>
                      {m.read_at ? `✓ ${t('msgSeen')} ${new Date(m.read_at).toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' })}` : t('msgPending')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TEST TAB */}
      {tab === 'test' && (
        <div style={{ maxWidth: 520 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{t('testTitle')}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18 }}>
              {t('testDesc')}
            </div>

            <Label>{t('fcmTokenLabel')}</Label>
            <textarea value={testToken} onChange={e => setTestToken(e.target.value)}
              placeholder={t('fcmTokenPlh')} rows={3}
              style={{ ...inputStyle, resize: 'none', fontFamily: 'monospace', fontSize: 11 }} />

            {testResult && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                background: testResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${testResult.ok ? 'var(--green)' : 'var(--red)'}`,
                color: testResult.ok ? 'var(--green)' : 'var(--red)',
              }}>{testResult.msg}</div>
            )}

            <button onClick={sendTest} disabled={testLoading || !testToken.trim() || !fcmStatus}
              style={{ ...btnStyle, width: '100%', opacity: testLoading || !testToken.trim() || !fcmStatus ? 0.5 : 1 }}>
              {testLoading ? t('sending') : t('sendTestBtn')}
            </button>

            {!fcmStatus && (
              <div style={{ marginTop: 10, fontSize: 11, color: 'var(--red)', textAlign: 'center' }}>
                {t('fcmNotConfiguredTest')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SUB-COMPONENTS ────────────────────────────────────────────────────────────

function FcmBadge({ configured }) {
  const { t } = useLang();
  if (configured === null) return null;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: configured ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
      border: `1px solid ${configured ? 'var(--green)' : 'var(--red)'}`,
      color: configured ? 'var(--green)' : 'var(--red)',
    }}>
      {configured ? t('fcmConnected') : t('fcmNotConfigured')}
    </span>
  );
}

function BroadcastTemplates({ onSelect }) {
  const { t } = useLang();
  const templates = [
    { title: '🎉 Yangi aksiya!', body: 'Bugun barcha paketlarga 20% chegirma! Tezroq boring.' },
    { title: '🏆 Turnir e\'loni', body: 'Yangi turnir boshlanmoqda! Ro\'yxatdan o\'ting va sovrinlar yuting.' },
    { title: '🔧 Texnik ishlar', body: 'Bugun 14:00–16:00 da texnik ishlar olib boriladi. Noqulaylik uchun uzr so\'raymiz.' },
    { title: '🎮 Yangi o\'yin!', body: 'Filialimizga yangi o\'yin qo\'shildi. Keling va sinab ko\'ring!' },
    { title: '💳 Balans eslatmasi', body: 'Balansni to\'ldiring va uzluksiz o\'ying. Minimum 5,000 so\'mdan.' },
    { title: '⏰ Ish vaqti', body: 'Biz 09:00–24:00 gacha ochiqmiz. Ko\'rishguncha!' },
  ];

  return (
    <div style={{ background: 'var(--bg2)', borderRadius: 14, padding: 24, border: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>{t('templatesTitle')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {templates.map((tpl, i) => (
          <button key={i} onClick={() => onSelect(tpl.title, tpl.body)} style={{
            textAlign: 'left', padding: '10px 14px', borderRadius: 8,
            background: 'var(--bg)', border: '1px solid var(--border)',
            cursor: 'pointer', color: 'var(--text2)', transition: 'border-color .15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cyan)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{tpl.title}</div>
            <div style={{ fontSize: 11, marginTop: 3, color: 'var(--text3)' }}>{tpl.body}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function NotifRow({ n }) {
  const { t } = useLang();
  const icon = TYPE_ICONS[(n.data?.type)] || '🔔';
  const isRead = n.is_read;
  const date = new Date(n.created_at);
  const dateStr = `${date.getDate().toString().padStart(2,'0')}.${(date.getMonth()+1).toString().padStart(2,'0')} `
    + `${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2,'0')}`;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      background: isRead ? 'var(--bg2)' : 'rgba(139,92,246,0.06)',
      border: `1px solid ${isRead ? 'var(--border)' : 'rgba(139,92,246,0.25)'}`,
      borderRadius: 10, padding: '12px 16px',
    }}>
      <span style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{n.title}</span>
          <span style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{dateStr}</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>{n.body}</div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11 }}>
          <span style={{ color: 'var(--text3)' }}>👤 {n.user_name}</span>
          <span style={{ color: 'var(--text3)' }}>{n.user_phone}</span>
          {!isRead && (
            <span style={{
              padding: '1px 7px', borderRadius: 10, fontSize: 10,
              background: 'rgba(139,92,246,0.15)', color: 'var(--purple)', fontWeight: 700
            }}>{t('newBadge')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
      <div>{text}</div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, marginTop: 14,
    textTransform: 'uppercase', letterSpacing: '1px' }}>{children}</div>;
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  background: 'var(--bg)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '10px 12px',
  color: 'var(--text)', fontSize: 13,
  outline: 'none',
};

const selectStyle = {
  ...inputStyle, cursor: 'pointer', marginBottom: 0,
};

const btnStyle = {
  background: 'linear-gradient(135deg, var(--cyan), var(--purple))',
  border: 'none', borderRadius: 8, padding: '10px 20px',
  color: '#000', fontWeight: 700, fontSize: 13, cursor: 'pointer',
};
